import React, { useState, useRef, useContext, useEffect } from 'react';
import './HeroSection.scss';
import RecipeBookIcon from '../assets/recipe-book.svg';
import RecipeSlider from './RecipeSlider';
import LoadingImage from '../assets/loading.gif';
import axios from 'axios';
import RecipeDetailsSection from './RecipeDetails';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './../components/AuthContext';
import { useUserId } from './../components/UserIdHook';
import GradientButton from './GradientButton';
import { useRecipeService, Recipe } from '../services/recipeService';
import SearchRecipeButton from './SearchButton';

interface ApiResponse {
  statusCode: number;
  body: string;
}



const HeroSection: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [inputText, setInputText] = useState<string>('A classic Italian pasta with a rich tomato sauce');
  const recipeDetailsRef = useRef<HTMLDivElement>(null);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const navigate = useNavigate();

  const { user } = useContext(AuthContext);
  const user_id = useUserId(user) ?? '';
  const { fetchRecipes } = useRecipeService();

  useEffect(() => {
    if (!user_id) {
      console.error('User ID is not available');
    }
    const loadRecipes = async () => {
      try {
          const { recipes: fetchedRecipes } = await fetchRecipes();
          setRecipes(fetchedRecipes);
      } catch (error) {
          console.error('Error loading recipes:', error);
      } 
  };
  loadRecipes();
  }, [user_id]);

  const handleCreateRecipe = async () => {
    setIsLoading(true);
    try {
      const requestBody = {
        prompt: inputText,
        content_type: 'json',
        bucket_name: 'privatebot-base',
        file_name: 'recipe.json',
      };
      const response = await axios.post<ApiResponse>(
        'https://1vllqk52u3.execute-api.us-east-1.amazonaws.com/v1/generate',
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const parsedResponse = JSON.parse(response.data.body);
      let contentUrl = parsedResponse.content_url;
      contentUrl = contentUrl.trim().replace(/```/g, '').replace(/\\n/g, '').replace(/\\"/g, '"');
      const recipeData = JSON.parse(contentUrl);
      recipeData.type = 'recipe';
      recipeData.user_id = user_id; // Assign the user_id to the recipe
      setRecipe(recipeData);

      if (recipeDetailsRef.current) {
        recipeDetailsRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (error) {
      alert(error);
      console.error('Error creating recipe:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigateToRecipes = () => {
    navigate('/recipes');
  };

  const handleNavigateToGenerateRecipe = () => {
    navigate('/generate');
  };


  const handleSaveRecipe = async () => {
    if (!recipe) return;

    const newId = recipe.id || new Date().toISOString().replace(/[-:.TZ]/g, '') + Math.floor(Math.random() * 1000);

    const requestBody = {
      entity: 'RecipeTable',
      action: 'upsert',
      pk_column: 'user_id',
      pk_value: user_id,
      sk_column: 'id',
      sk_value: newId.toString(),
      columns: {
        row_type: 'recipe',
        recipe_name: recipe.recipe_name || '',
        description: recipe.description || '',
        ingredients: JSON.stringify(recipe.ingredients),
        instructions: JSON.stringify(recipe.instructions),
        tried: recipe.tried ? 'true' : 'false',
        average_rating: recipe.average_rating?.toString() || '0',
        rating_count: recipe.rating_count?.toString() || '0',
      },
    };

    try {
      const response = await axios.post(
        'https://dw1271o3wa.execute-api.us-east-1.amazonaws.com/v1/tablefields',
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const { statusCode, body } = response.data;

      if (statusCode === 200) {
        const savedRecipeId = newId;
        navigate(`/blog-recipe/${savedRecipeId}`);
      } else {
        const errorMessage = JSON.parse(body).message;
        alert(`Error saving recipe: ${errorMessage} ` + JSON.stringify(requestBody));
        console.error(`Error saving recipe: ${errorMessage}`);
      }
    } catch (error) {
      alert('Error saving recipe. Please try again.' + error + ' ' + JSON.stringify(requestBody));
      console.error('Error saving recipe:', error);
    }
  };

  const handleIngredientChange = (index: number, value: string) => {
    if (!recipe) return;
    const updatedIngredients = [...recipe.ingredients];
    updatedIngredients[index] = value;
    setRecipe({ ...recipe, ingredients: updatedIngredients });
  };

  const handleInstructionChange = (index: number, value: string) => {
    if (!recipe) return;
    const updatedInstructions = [...recipe.instructions];
    updatedInstructions[index] = value;
    setRecipe({ ...recipe, instructions: updatedInstructions });
  };

  return (
    <section className="hero">
      <div className="hero-content">
        <img src={RecipeBookIcon} alt="Recipe Book Icon" className="hero-icon" />
        <h1>Discover & Plan Your Perfect Meal</h1>
        <p>Create recipes, plan meals, and make grocery lists with ease.</p>

        <div className="recipe-input-section">
          <SearchRecipeButton className="cta-button" onClick={handleNavigateToRecipes}>
            Find a Recipe
          </SearchRecipeButton>
          <GradientButton className="cta-button" onClick={handleNavigateToGenerateRecipe}>
            Generate a Recipe
          </GradientButton>
        </div>
      </div>

      <div className="recipe-display">
        {isLoading ? (
          <div className="loading">
            <img src={LoadingImage} alt="Loading..." />
          </div>
        ) : (
          <RecipeSlider />
        )}
      </div>

      {recipe && (
        <section className="recipe-details-section" ref={recipeDetailsRef}>
          <button className="save-button" onClick={handleSaveRecipe}>
            Save Recipe
          </button>
          <RecipeDetailsSection
            recipe={recipe as Recipe}
            isEditing={false}
            onSaveRecipe={handleSaveRecipe}
            onEdit={() => {}}
            onIngredientChange={handleIngredientChange}
            onInstructionChange={handleInstructionChange}
          />
        </section>
      )}
    </section>
  );
};

export default HeroSection;
